/**
 * Icon set — thin wrappers over lucide-react.
 *
 * Historically these were hand-drawn SVGs. They now re-export lucide icons under
 * the app's existing names so the ~25 call sites keep working unchanged while the
 * whole app renders a single, consistent icon family. The wrapper preserves the
 * legacy IconProps shape ({ size, strokeWidth, className }).
 */
import React from 'react';
import {
  Pencil,
  Trash2,
  Check,
  X,
  Plus,
  CheckCircle2,
  Clock,
  History,
  Info,
  AlertTriangle,
  File,
  FileText,
  Folder,
  Paperclip,
  Users,
  User,
  MessageSquare,
  Send,
  Zap,
  ChevronRight,
  BarChart3,
  Sun,
  Moon,
  Copy,
  Calendar,
  Bell,
  Search,
  Camera,
  Download,
  Image as ImageIco,
  LogOut,
  LayoutDashboard,
  LayoutPanelLeft,
  Settings,
  BookOpen,
  Menu,
  LucideIcon,
} from 'lucide-react';

export interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
}

// Build a legacy-named component from a lucide icon, keeping our default size/stroke.
const wrap = (Lucide: LucideIcon, defaultSize = 16, defaultStroke = 2) => {
  const Comp: React.FC<IconProps> = ({ size = defaultSize, strokeWidth = defaultStroke, className }) => (
    <Lucide size={size} strokeWidth={strokeWidth} className={className} />
  );
  return Comp;
};

export const EditIcon = wrap(Pencil, 14);
export const DeleteIcon = wrap(Trash2, 14);
export const SaveIcon = wrap(Check, 14);
export const CancelIcon = wrap(X, 14);
export const PlusIcon = wrap(Plus, 12, 2.5);
export const CheckCircleIcon = wrap(CheckCircle2, 20);
export const ClockIcon = wrap(Clock, 16);
export const HistoryIcon = wrap(History, 16);
export const InfoIcon = wrap(Info, 16);
export const WarningIcon = wrap(AlertTriangle, 20);
export const FileIcon = wrap(File, 20);
export const FileTextIcon = wrap(FileText, 16);
export const FolderIcon = wrap(Folder, 16);
export const PaperclipIcon = wrap(Paperclip, 16);
export const UsersIcon = wrap(Users, 20);
export const UserIcon = wrap(User, 16);
export const ChatIcon = wrap(MessageSquare, 16);
export const SendIcon = wrap(Send, 16);
export const BoltIcon = wrap(Zap, 16);
export const ChevronRightIcon = wrap(ChevronRight, 14);
export const BarChartIcon = wrap(BarChart3, 20);
export const SunIcon = wrap(Sun, 18);
export const MoonIcon = wrap(Moon, 18);
export const CopyIcon = wrap(Copy, 14);
export const CalendarIcon = wrap(Calendar, 16);
export const BellIcon = wrap(Bell, 18);
export const SearchIcon = wrap(Search, 18);
export const CameraIcon = wrap(Camera, 16);
export const DownloadIcon = wrap(Download, 16);
export const ImageIcon = wrap(ImageIco, 16);
export const LogoutIcon = wrap(LogOut, 16);
export const DashboardIcon = wrap(LayoutDashboard, 28);
export const WorkspaceIcon = wrap(LayoutPanelLeft, 20);
export const SettingsIcon = wrap(Settings, 18);
export const BookOpenIcon = wrap(BookOpen, 18);
export const MenuIcon = wrap(Menu, 20);
